import React, { useCallback } from 'react';
import { useNavigate }        from 'react-router-dom';
import { Box }                from '@mui/material';

import { useSite }       from '../context/SiteContext';
import useAuthGuard      from '../hooks/useAuthGuard';
import { LobbyEngine }   from './engine/index';
import { logoutParticipant } from '../services/api';

// ── LobbyPage ─────────────────────────────────────────────────────────────────
// Thin shell that:
//   1. Guards the route (redirects to login if unauthenticated)
//   2. Resolves the start lobby ID and participant info from context/storage
//   3. Renders the full-screen LobbyEngine
//
// `preloadedLobby` is the lobby object returned by the resolver (result.data).
// It gives us the startLobbyId so the engine can fetch the full manifest from
// its own /api/lobby-engine/:id endpoint.
// ─────────────────────────────────────────────────────────────────────────────
const LobbyPage = ({ preloadedLobby, requireLogin = true }) => {
  const { event, pageReadyRef } = useSite();
  const navigate = useNavigate();

  // ── Auth guard ──────────────────────────────────────────────────────────────
  const { authChecked } = useAuthGuard(event, requireLogin);

  // ── Participant info ────────────────────────────────────────────────────────
  const participantName = event?.participant?.name
    || (() => {
         try {
           const raw = localStorage.getItem('govirtual_user') || sessionStorage.getItem('govirtual_user');
           if (raw) {
             const u = JSON.parse(raw);
             return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || null;
           }
         } catch {}
         return null;
       })()
    || 'Guest';

  // ── Logout handler ──────────────────────────────────────────────────────────
  const handleLogout = useCallback(async () => {
    try { await logoutParticipant(); } catch {}
    ['govirtual_token', 'govirtual_user'].forEach(k => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    const loginPath = event?.urlMode === 'flat'
      ? '/login'
      : `/${event?.slug || ''}/login`;
    navigate(loginPath, { replace: true });
  }, [event, navigate]);

  // ── Signal page ready to the shell transition system ───────────────────────
  // Called once auth is confirmed so LobbyTransition overlays can fade out.
  React.useEffect(() => {
    if (authChecked) {
      requestAnimationFrame(() => requestAnimationFrame(() => pageReadyRef?.current?.()));
    }
  }, [authChecked, pageReadyRef]);

  // Don't render the engine until auth is resolved (avoids a flash of the
  // engine UI before a potential redirect to login)
  if (!authChecked || !preloadedLobby?._id) return null;

  return (
    <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'fixed', inset: 0 }}>
      <LobbyEngine
        startLobbyId={String(preloadedLobby._id)}
        participantName={participantName}
        onLogout={handleLogout}
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
};

export default LobbyPage;